export type CandidateTab = 'new' | 'backlog' | 'snoozed' | 'rejected' | 'done' | 'gone'

export type MatchReason = 'name_exact' | 'name_fuzzy' | 'republish_pair'

export interface Candidate {
  candidate_id: number
  broker_id: number
  broker_name: string
  host: string
  source_key: string
  canonical_url: string
  name: string | null
  city: string | null
  district: string | null
  neighbourhood: string | null
  status_raw: string | null
  type_raw: string | null
  total_fractions: number | null
  available_fractions: number | null
  is_live: boolean | null
  scrapable: boolean
  needs_enrichment: boolean
  flags: string[]
  published_at: string | null
  first_seen_at: string
  last_seen_at: string
  is_backlog: boolean
  review_status: string
  review_reason: string | null
  review_note: string | null
  reviewed_at: string | null
  snoozed_until: string | null
  auto_snoozed: boolean
  project_id: number | null
  match_project_id: number | null
  match_reason: MatchReason | null
  match_score: number | null
  match_name: string | null
  match_website: string | null
  match_sold_out: boolean
  match_broker_name: string | null
}

export interface HostState {
  host: string
  broker_id: number
  broker_name: string | null
  baselined_at: string | null
  last_run_at: string | null
  last_success_at: string | null
  last_error: string | null
  last_item_count: number | null
}

export interface DiscoveryMeta {
  counts: {
    new: number
    backlog: number
    snoozed: number
    rejected: number
    auto_rejected: number
    done: number
    gone: number
  }
  last_run: {
    run_id: string
    trigger: 'scheduler' | 'manual'
    status: 'running' | 'completed' | 'partial' | 'failed'
    started_at: string
    finished_at: string | null
    hosts: Record<string, { ok: boolean; created: number; error?: string; bulk?: boolean; suspicious?: boolean }>
  } | null
  hosts: HostState[]
}

export interface ListResponse {
  items: Candidate[]
  total: number
  page: number
  limit: number
  tab: string
}

export const REJECT_REASONS: { value: string; label: string }[] = [
  { value: 'sold_out', label: 'Sold out / no units' },
  { value: 'not_residential', label: 'Not residential' },
  { value: 'duplicate', label: 'Duplicate of another project' },
  { value: 'out_of_scope', label: 'Out of scope (resort, hotel, branded)' },
  { value: 'other', label: 'Other' },
]

/** Pulls the human message (and structured fields) out of an axios error. */
export function apiError(error: any): { message: string; code?: string; suggested_name?: string; data?: any } {
  const data = error?.response?.data
  const list: string[] | undefined = Array.isArray(data?.errors) ? data.errors : undefined
  return {
    message: list?.length ? list.join('; ') : data?.message || error?.message || 'Request failed',
    code: data?.code,
    suggested_name: data?.suggested_name,
    data,
  }
}

/** YYYY-MM-DD on the Lisbon wall clock (the team's calendar and the backend's listing_date). */
export const lisbonDay = (date: Date): string => date.toLocaleDateString('en-CA', { timeZone: 'Europe/Lisbon' })
